import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, FormControl, FormHelperText, OutlinedInput, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFeathers } from '../../app/util';
import { userLogin } from '../../app/actions';
import { PhotoUploadInput, Title, UserAvatar } from '../../components';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { PatchUser, User } from '../../app/feathers/users/User';

type RootState = {
  user: User;
};

const Profile = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const canEditPublicProfile = (user?.permissions || []).some((permission: string) =>
    ['editor', 'admin', 'super-admin'].includes(permission)
  );

  const [ firstName, setFirstName ] = useState('');
  const [ lastName, setLastName ] = useState('');
  const [ photo, setPhoto ] = useState<string | null>(null);
  const [ publicBio, setPublicBio ] = useState('');
  const [ publicRole, setPublicRole ] = useState('');
  const [ isEditing, setIsEditing ] = useState(false);
  const [ saving, setSaving ] = useState(false);
  const [ saveStatus, setSaveStatus ] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const resetFormFromUser = () => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setPhoto(user?.photo || null);
    setPublicBio(user?.publicBio || '');
    setPublicRole(user?.publicRole || '');
  };

  useEffect(() => {
    resetFormFromUser();
  }, [user?._id, user?.firstName, user?.lastName, user?.photo, user?.publicBio, user?.publicRole]);

  const fullName = useMemo(() => {
    const merged = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    return merged || user?.name || '--';
  }, [user?.firstName, user?.lastName, user?.name]);

  const publicRoleDisplay = (user?.publicRole || '').trim()
    || ((user?.permissions || []).some((permission: string) => ['publisher', 'editor', 'super-admin'].includes(permission))
      ? 'Senior researcher'
      : 'Researcher');

  const hasChanges = useMemo(() => {
    const basicChanged = (
      (firstName || '') !== (user?.firstName || '')
      || (lastName || '') !== (user?.lastName || '')
      || (photo || null) !== (user?.photo || null)
    );
    const publicProfileChanged = (
      (publicBio || '') !== (user?.publicBio || '')
      || (publicRole || '') !== (user?.publicRole || '')
    );
    return canEditPublicProfile ? (basicChanged || publicProfileChanged) : basicChanged;
  }, [
    firstName,
    lastName,
    photo,
    publicBio,
    publicRole,
    canEditPublicProfile,
    user?.firstName,
    user?.lastName,
    user?.photo,
    user?.publicBio,
    user?.publicRole
  ]);

  const onSave = async () => {
    if(!user?._id || !feathers || saving || !hasChanges || !isEditing) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      const patchBody: PatchUser = {
        firstName,
        lastName,
      };
      if(photo) {
        patchBody.photo = photo;
      }
      if(canEditPublicProfile) {
        patchBody.publicBio = publicBio;
        patchBody.publicRole = publicRole;
      }
      const updatedUser = await feathers.getService('users').patch(user._id, patchBody);
      dispatch(userLogin(updatedUser));
      setSaveStatus({ type: 'success', message: 'Profile updated.' });
      setIsEditing(false);
    } catch(err) {
      console.error('Error updating profile', err);
      setSaveStatus({ type: 'error', message: 'Unable to save profile right now.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <Title>Your Profile</Title>
      <Typography variant='h1'>Your Profile</Typography>
      <Typography variant='body1' style={{ maxWidth: '800px' }}>
        This information controls how your account appears across internal tools and the public team page.
      </Typography>
      <ProfileCard>
        {
          !isEditing
          ? (
            <ProfileView>
              <ProfileIdentity>
                <UserAvatar user={user} size={84} />
                <div>
                  <Typography variant='h3'>{fullName}</Typography>
                  <Typography variant='body1' style={{ opacity: 0.75 }}>{publicRoleDisplay}</Typography>
                </div>
              </ProfileIdentity>
              <InfoBlock>
                <InfoLabel>Public Bio</InfoLabel>
                <Typography variant='body1'>{(user?.publicBio || '').trim() || '--'}</Typography>
              </InfoBlock>
            </ProfileView>
          )
          : (
            <>
              <FormControl>
                <FormHelperText>Profile photo</FormHelperText>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 1 }}>
                  <PhotoUploadInput
                    style={{}}
                    value={photo}
                    onChange={setPhoto}
                    uploadType='userPhoto'
                    invitationId={undefined}
                  />
                </Box>
              </FormControl>
              <FieldGrid>
                <FormControl>
                  <FormHelperText>First Name</FormHelperText>
                  <OutlinedInput
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormHelperText>Last Name</FormHelperText>
                  <OutlinedInput
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                  />
                </FormControl>
              </FieldGrid>
              {
                canEditPublicProfile && (
                  <>
                    <FormControl>
                      <FormHelperText>Public Role</FormHelperText>
                      <OutlinedInput
                        value={publicRole}
                        onChange={e => setPublicRole(e.target.value)}
                        placeholder='Shown as your role subtitle on the About page.'
                      />
                    </FormControl>
                    <FormControl>
                      <FormHelperText>Public Bio</FormHelperText>
                      <OutlinedInput
                        value={publicBio}
                        onChange={e => setPublicBio(e.target.value)}
                        multiline
                        minRows={6}
                        placeholder='Share a short public background for the About page.'
                      />
                    </FormControl>
                  </>
                )
              }
            </>
          )
        }
        {
          saveStatus?.message &&
          <SaveStatus $isError={saveStatus.type === 'error'}>{saveStatus.message}</SaveStatus>
        }
        {
          !isEditing
          ? (
            <Button
              variant='contained'
              color='primary'
              onClick={() => {
                setSaveStatus(null);
                setIsEditing(true);
              }}
            >
              Edit profile
            </Button>
          )
          : (
            <ActionRow>
              <Button
                color='inherit'
                variant='outlined'
                onClick={() => {
                  resetFormFromUser();
                  setSaveStatus(null);
                  setIsEditing(false);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={onSave}
                disabled={saving || !hasChanges}
              >
                {saving ? 'Saving...' : 'Save profile'}
              </Button>
            </ActionRow>
          )
        }
      </ProfileCard>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: calc(100% - 64px);
  min-height: calc(100% - 64px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
  padding: 32px;
`;

const ProfileCard = styled.div`
  width: 100%;
  max-width: 860px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(17, 24, 39, 0.08);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media(max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const SaveStatus = styled.div<{ $isError: boolean }>`
  color: ${props => props.$isError ? '#b42318' : '#027a48'};
  font-weight: 600;
`;

const ProfileView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ProfileIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoLabel = styled.div`
  font-size: 13px;
  opacity: 0.7;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

export default Profile;
